import type { Point } from './viewport';
import { screenDeltaToStageDelta } from './viewport';

export type ElementPosition = Point;

export type DragDraft = {
  elementIds: string[];
  startPositions: Record<string, ElementPosition>;
  stageDelta: Point;
  cancelled: boolean;
};

/** Screen px the pointer must travel before a press turns into a drag, so click jitter moves nothing. */
const DRAG_START_THRESHOLD_PX = 4;

export function exceedsDragThreshold(from: Point, to: Point): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) >= DRAG_START_THRESHOLD_PX;
}

export function beginDrag(
  elementIds: string[],
  startPositions: Record<string, ElementPosition>,
): DragDraft {
  return {
    elementIds,
    startPositions,
    stageDelta: { x: 0, y: 0 },
    cancelled: false,
  };
}

export function updateDragFromScreenDelta(
  draft: DragDraft,
  screenDelta: Point,
  zoom: number,
): DragDraft {
  if (draft.cancelled) {
    return draft;
  }
  const increment = screenDeltaToStageDelta(screenDelta, zoom);
  return {
    ...draft,
    stageDelta: {
      x: draft.stageDelta.x + increment.x,
      y: draft.stageDelta.y + increment.y,
    },
  };
}

export function cancelDrag(draft: DragDraft): DragDraft {
  return { ...draft, cancelled: true, stageDelta: { x: 0, y: 0 } };
}

/** True when the gesture ended where it started (for example a plain click). */
export function isZeroDelta(draft: DragDraft): boolean {
  return draft.stageDelta.x === 0 && draft.stageDelta.y === 0;
}

/**
 * Document positions after pointer-up.
 * Null when cancelled or when nothing moved, so a click never creates a history entry.
 */
export function commitDrag(draft: DragDraft): Record<string, ElementPosition> | null {
  if (draft.cancelled || isZeroDelta(draft)) {
    return null;
  }

  const result: Record<string, ElementPosition> = {};
  for (const id of draft.elementIds) {
    const start = draft.startPositions[id];
    if (!start) continue;
    result[id] = {
      x: start.x + draft.stageDelta.x,
      y: start.y + draft.stageDelta.y,
    };
  }
  return result;
}

export function previewDragPosition(draft: DragDraft, elementId: string): ElementPosition | null {
  const start = draft.startPositions[elementId];
  if (!start || draft.cancelled) {
    return start ?? null;
  }
  return {
    x: start.x + draft.stageDelta.x,
    y: start.y + draft.stageDelta.y,
  };
}
